// API utility functions for handling authorization and requests

interface ApiResponse<T = any> {
  data: T;
  status: number;
  message: string;
}

interface ApiError {
  message: string;
  status: number;
  details?: any;
}

import { getAuthToken, setAuthToken, clearAuthToken } from './authToken';
import { getApiBaseUrl } from '@/config';

/** Get the API base URL from centralized config. Re-exported for convenience. */
export const getBaseUrl = (): string => getApiBaseUrl();

// Check if token is expired (basic check)
export const isTokenExpired = (): boolean => {
  const token = localStorage.getItem('token');
  if (!token) return true;
  
  try {
    // Try to decode JWT token to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    // If we can't decode the token, assume it's valid
    return false;
  }
};

// Handle token refresh (if needed)
export const refreshToken = async (): Promise<boolean> => {
  try {
    const baseUrl = getBaseUrl();
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      console.warn('No refresh token available');
      return false;
    }

    const response = await fetch(`${baseUrl}/Token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': 'Basic cGF2YW46cGs='
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken
      })
    });

    if (response.ok) {
      const data = await response.json();
      setAuthToken(data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return false;
  }
};

// Make an authenticated API request with automatic token refresh
export const authenticatedFetch = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const baseUrl = getBaseUrl();
  const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;
  
  // Prepare headers
  const headers = new Headers(options.headers);
  headers.set('Authorization', getAuthToken());
  headers.set('Content-Type', 'application/json');
  headers.set('Accept', 'application/json');
  
  // First attempt
  let response = await fetch(url, {
    ...options,
    headers
  });
  
  // If unauthorized, try to refresh token and retry once
  if (response.status === 401) {
    console.log('Token expired, attempting refresh...');
    const refreshSuccess = await refreshToken();
    
    if (refreshSuccess) {
      // Retry with new token
      headers.set('Authorization', getAuthToken());
      response = await fetch(url, {
        ...options,
        headers
      });
      
      // If still 401 after refresh, force logout
      if (response.status === 401) {
        console.log('Token refresh failed, forcing logout');
        clearAuthToken();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('username');
        window.location.href = '/';
        throw new Error('Authentication failed. Please login again.');
      }
    } else {
      // Refresh failed - force logout immediately
      console.log('Token refresh failed, forcing logout');
      clearAuthToken();
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('username');
      window.location.href = '/';
      throw new Error('Authentication failed. Please login again.');
    }
  }
  
  if (!response.ok) {
    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { message: errorText };
    }
    
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }
  
  // Try to parse JSON response
  let data: T;
  const contentType = response.headers.get('content-type');
  
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    // For non-JSON responses (like PDFs), return the response as data
    data = response as any;
  }
  
  return {
    data,
    status: response.status,
    message: 'Success'
  };
};

// Specialized function for PDF requests
export const fetchPDF = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Blob> => {
  const baseUrl = getBaseUrl();
  const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;
  
  // Prepare headers for PDF request
  const headers = new Headers(options.headers);
  const authToken = getAuthToken();
  headers.set('Authorization', authToken);
  headers.set('Accept', 'application/pdf');
  
  console.log('PDF Request Debug:');
  console.log('URL:', url);
  console.log('Auth Token:', authToken ? 'Token present' : 'No token');
  console.log('Headers:', Object.fromEntries(headers.entries()));
  
  // First attempt
  let response = await fetch(url, {
    ...options,
    headers
  });
  
  console.log('PDF Response Debug:');
  console.log('Status:', response.status);
  console.log('Status Text:', response.statusText);
  console.log('Response Headers:', Object.fromEntries(response.headers.entries()));
  
  // If unauthorized, try to refresh token and retry once
  if (response.status === 401) {
    console.log('Token expired, attempting refresh for PDF...');
    const refreshSuccess = await refreshToken();
    
    if (refreshSuccess) {
      // Retry with new token
      headers.set('Authorization', getAuthToken());
      response = await fetch(url, {
        ...options,
        headers
      });
      
      // If still 401 after refresh, force logout
      if (response.status === 401) {
        console.log('Token refresh failed for PDF, forcing logout');
        clearAuthToken();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('username');
        window.location.href = '/';
        throw new Error('Authentication failed. Please login again.');
      }
    } else {
      // Refresh failed - force logout immediately
      console.log('Token refresh failed for PDF, forcing logout');
      clearAuthToken();
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('username');
      window.location.href = '/';
      throw new Error('Authentication failed. Please login again.');
    }
  }
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText} - ${errorText}`);
  }
  
  // Check if the response is actually a PDF
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/pdf')) {
    throw new Error('Response is not a PDF file');
  }
  
  return await response.blob();
};

// Utility to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return !!token && !isTokenExpired();
};

// Utility to logout user
export const logout = (): void => {
  clearAuthToken();
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('username');
  window.location.href = '/';
};
